import { trackSectionCtaClick } from "@/lib/analytics";

const SERVICE_IMAGES = {
  agentic: { src: "/media/service-agentic-ai-iso-final_9db4b44b.png" },
  seo: { src: "/media/service-aeo-seo-iso-final_880c247b.png" },
  ppc: { src: "/media/service-ppc-iso-final_ee560a32.png" },
  ecommerce: { src: "/media/service-ppc-iso-final_ee560a32.png" },
};

const coreServices = [
  {
    id: "agentic",
    label: "Agentic AI",
    title: "Agentic AI Lead Generation",
    imageKey: "agentic" as const,
    description: "Human-governed agent workflows for account research, qualification and commercially useful follow-through.",
    url: "/services/gemini-agentic-ai/",
    accent: "#FF8C00",
  },
  {
    id: "seo",
    label: "AI Search",
    title: "SEO, AEO and GEO",
    imageKey: "seo" as const,
    description: "Evidence-led information architecture, content and technical work for organic and answer-engine discovery.",
    url: "/services/seo-aeo-geo/",
    accent: "#00D4AA",
  },
  {
    id: "ppc",
    label: "Paid Growth",
    title: "PPC and Conversion Optimisation",
    imageKey: "ppc" as const,
    description: "Persona-led paid-media and conversion decisions that can be measured against commercial outcomes.",
    url: "/services/ppc-performance-max/",
    accent: "#A78BFA",
  },
  {
    id: "ecommerce",
    label: "Ecommerce",
    title: "AI Marketing for Ecommerce",
    imageKey: "ecommerce" as const,
    description: "Shopping, paid media, product-page clarity and AI-search work judged against profitable sales.",
    url: "/services/ai-marketing-agency-for-ecommerce/",
    accent: "#00A6A6",
  },
];

export default function Services() {
  return (
    <section className="bg-[#FAF9F7] py-14 sm:py-16 lg:py-20" aria-labelledby="services-heading" id="services" itemScope itemType="https://schema.org/ItemList">
      <div className="container">
        <div className="max-w-3xl">
          <span className="text-mono text-[#FF8C00] block mb-3">What we do</span>
          <h2 id="services-heading" className="text-display text-3xl sm:text-4xl lg:text-5xl text-[#1A1A2E] speakable-services">Four connected services. One accountable growth programme.</h2>
          <p className="mt-4 text-[#6B7280] text-base lg:text-lg">Choose the starting point that fits the commercial question. We coordinate the work around one persona, evidence base and measurement plan rather than a stack of isolated deliverables.</p>
        </div>

        <div className="mt-9 grid gap-5 lg:grid-cols-2 xl:grid-cols-4">
          {coreServices.map((service) => (
            <article key={service.id} className="flex flex-col overflow-hidden rounded-sm border border-[#E8E4DF] bg-white" itemScope itemType="https://schema.org/Service" style={{ borderTopColor: service.accent, borderTopWidth: "3px" }}>
              <div className="aspect-[16/9] bg-gradient-to-br from-[#f0f4ff] to-[#e8f5f2]">
                <img src={SERVICE_IMAGES[service.imageKey].src} alt="" className="h-full w-full object-contain p-3" loading="lazy" width={900} height={506} decoding="async" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="text-mono text-xs uppercase tracking-widest" style={{ color: service.accent }}>{service.label}</span>
                <h3 className="mt-2 text-display text-2xl text-[#1A1A2E]" itemProp="name">{service.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4B5563]" itemProp="description">{service.description}</p>
                <a href={service.url} onClick={() => trackSectionCtaClick({ section_name: "services", cta_text: `Explore ${service.title}`, destination: service.url })} className="mt-6 inline-flex text-sm font-semibold underline underline-offset-4 transition-colors hover:opacity-75" style={{ color: service.accent }}>Explore the service →</a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 flex flex-col justify-between gap-4 rounded-sm border border-[#E8E4DF] bg-white p-5 sm:flex-row sm:items-center sm:p-6">
          <div>
            <h3 className="font-serif text-xl text-[#1A1A2E]">Need a tailored combination?</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">See the full service directory or begin with a high-level AI visibility baseline.</p>
          </div>
          <div className="flex flex-wrap gap-4 text-sm font-semibold">
            <a href="/services/" onClick={() => trackSectionCtaClick({ section_name: "services", cta_text: "View all services", destination: "/services/" })} className="text-[#C65F00] underline underline-offset-4 hover:text-[#8B4300]">View all services</a>
            <a href="/free-ai-growth-audit/" onClick={() => trackSectionCtaClick({ section_name: "services", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })} className="text-[#C65F00] underline underline-offset-4 hover:text-[#8B4300]">Check my AI visibility</a>
          </div>
        </div>
      </div>
    </section>
  );
}
